import React, { useEffect, useId, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCamera, faImages } from '@fortawesome/free-solid-svg-icons'
import PhotoCapture from './PhotoCapture'
import PhotoGalleryFrame, { photoGallerySafeIndex } from './PhotoGalleryFrame'

type PhotoUploadPreviewProps = {
  images: string[]
  alt?: string
  disabled?: boolean
  /** Gallery / file picker uploads. */
  onUpload: (files: FileList | null) => void
  /** In-app camera captures (JPEG data URLs, already sized). */
  onAddDataUrls?: (dataUrls: string[]) => void
  onRemove?: (idx: number) => void
  /** When photos exist, tapping the current photo opens this instead of adding. */
  onPreview?: (index: number) => void
  /** square = modal thumbnail; wide = add/edit section */
  variant?: 'square' | 'wide'
  galleryIndex?: number
  onGalleryIndexChange?: (index: number) => void
  /** Show DaisyUI file-input on desktop instead of outline button */
  desktopControl?: 'button' | 'file-input'
}

export const PhotoUploadPreview: React.FC<PhotoUploadPreviewProps> = ({
  images,
  alt = 'Photo',
  disabled = false,
  onUpload,
  onAddDataUrls,
  onRemove,
  onPreview,
  variant = 'wide',
  galleryIndex: controlledIndex,
  onGalleryIndexChange,
  desktopControl = 'button',
}) => {
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [cameraOpen, setCameraOpen] = useState(false)
  const [localIndex, setLocalIndex] = useState(0)
  const prevCountRef = useRef(images.length)
  const controlled = typeof controlledIndex === 'number' && typeof onGalleryIndexChange === 'function'
  const rawIndex = controlled ? controlledIndex : localIndex
  const safeIndex = photoGallerySafeIndex(rawIndex, images.length)
  const canUseLiveCamera =
    typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia) && Boolean(onAddDataUrls)

  const setIndex = (next: number) => {
    if (!images.length) return
    const wrapped = photoGallerySafeIndex(next, images.length)
    if (controlled) onGalleryIndexChange(wrapped)
    else setLocalIndex(wrapped)
  }

  useEffect(() => {
    const prev = prevCountRef.current
    prevCountRef.current = images.length
    if (!images.length) {
      if (controlled) onGalleryIndexChange(0)
      else setLocalIndex(0)
      return
    }
    if (images.length > prev) {
      setIndex(images.length - 1)
      return
    }
    if (safeIndex >= images.length) setIndex(images.length - 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to image list length changes
  }, [images.length])

  const openGallery = () => {
    if (disabled) return
    inputRef.current?.click()
  }

  const openCamera = () => {
    if (disabled || !canUseLiveCamera) return
    setCameraOpen(true)
  }

  const onPrimaryTap = () => {
    if (disabled) return
    if (canUseLiveCamera) openCamera()
    else openGallery()
  }

  const previewOnTap = Boolean(onPreview) && images.length > 0
  const onFrameTap = () => {
    if (disabled) return
    if (previewOnTap) {
      onPreview?.(safeIndex)
      return
    }
    onPrimaryTap()
  }

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpload(e.target.files)
    e.currentTarget.value = ''
  }

  return (
    <div className="flex flex-col items-center gap-3 w-full">
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept="image/*"
        multiple
        className="sr-only"
        disabled={disabled}
        onChange={onFileChange}
      />

      <PhotoGalleryFrame
        images={images}
        index={safeIndex}
        onIndexChange={setIndex}
        alt={alt}
        variant={variant}
        emptyLabel={canUseLiveCamera ? 'Tap to take photo' : 'Tap to add photo'}
        className={variant === 'square' ? 'mb-3' : ''}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={onFrameTap}
          className={`absolute inset-0 z-[5] ${
            disabled ? 'cursor-not-allowed' : 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary'
          }`}
          aria-label={previewOnTap ? 'View photo' : images.length ? 'Add more photos' : 'Take photo'}
        />
        {!disabled && !previewOnTap && images.length <= 1 ? (
          <span className="pointer-events-none absolute bottom-2 right-2 z-10 sm:hidden badge badge-sm badge-neutral gap-1 shadow">
            <FontAwesomeIcon icon={faCamera} />
            {canUseLiveCamera ? 'Camera' : 'Add'}
          </span>
        ) : null}
      </PhotoGalleryFrame>

      <div className="flex w-full gap-2">
        {previewOnTap ? (
          <button
            type="button"
            className={`btn btn-outline flex-1 ${disabled ? 'btn-disabled' : ''}`}
            disabled={disabled}
            onClick={() => onPreview?.(safeIndex)}
          >
            View photo
          </button>
        ) : null}
        {canUseLiveCamera ? (
          <>
            <button
              type="button"
              className={`btn btn-outline flex-1 ${disabled ? 'btn-disabled' : ''}`}
              disabled={disabled}
              onClick={openCamera}
            >
              <FontAwesomeIcon icon={faCamera} />
              Take photo
            </button>
            <button
              type="button"
              className={`btn btn-outline flex-1 ${disabled ? 'btn-disabled' : ''}`}
              disabled={disabled}
              onClick={openGallery}
            >
              <FontAwesomeIcon icon={faImages} />
              Gallery
            </button>
          </>
        ) : desktopControl === 'button' ? (
          <button
            type="button"
            className={`btn btn-outline flex-1 ${previewOnTap ? '' : 'hidden sm:inline-flex'} ${disabled ? 'btn-disabled' : ''}`}
            disabled={disabled}
            onClick={openGallery}
          >
            <FontAwesomeIcon icon={faCamera} />
            Take / upload photos
          </button>
        ) : null}
      </div>

      {desktopControl === 'file-input' && !canUseLiveCamera ? (
        <div className="hidden sm:block w-full max-w-md">
          <input
            type="file"
            accept="image/*"
            multiple
            className="file-input file-input-bordered file-input-sm w-full"
            disabled={disabled}
            onChange={onFileChange}
          />
        </div>
      ) : null}

      {images.length > 0 && onRemove ? (
        <div className="flex flex-wrap gap-2 w-full">
          {images.map((_, idx) => (
            <button
              key={idx}
              type="button"
              className="btn btn-xs btn-ghost"
              disabled={disabled}
              onClick={() => onRemove(idx)}
            >
              Remove photo #{idx + 1}
            </button>
          ))}
        </div>
      ) : null}

      {canUseLiveCamera && onAddDataUrls ? (
        <PhotoCapture
          open={cameraOpen}
          disabled={disabled}
          onClose={() => setCameraOpen(false)}
          onCapture={(dataUrl) => onAddDataUrls([dataUrl])}
          onPickGallery={openGallery}
        />
      ) : null}
    </div>
  )
}

export default PhotoUploadPreview
